import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { BasicTabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { confirmDestructive } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { CancelAttestationModal, OverviewTab, ReviewTab, SubmissionTab } from '@client/components/attestations';
import { AttestationReminderButton } from '@client/components/attestations/reporting/AttestationReminderButton';
import { validateReviewCommentForm } from '@client/components/attestations/review-form-validation';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Typography } from 'antd';
import { Ban, RefreshCw } from 'lucide-react';
import { useState } from 'react';

const { Text } = Typography;

export const Route = createFileRoute('/console/$orgId/attestations/user-attestations/$attestationId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'User attestation details',
        },
        roles: ['attestations_user', 'org_admin'],
    },
    params: {
        parse({ attestationId }) {
            return { attestationId: BigInt(attestationId) };
        },
        stringify({ attestationId }) {
            return { attestationId: String(attestationId) };
        },
    },
    component: UserAttestationDetail,
});

function UserAttestationDetail() {
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { attestationId } = Route.useParams();
    const formatter = useFormatter();
    const [activeTab, setActiveTab] = useState('overview');
    const [reviewForm] = Form.useForm<{ comment: string }>();
    const formDirty = useFormDirty();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const { can } = usePermissions();
    const reportingEnabled = useFeatureFlag('enable-attestation-reporting', false);

    // Queries
    const { data: attestation, isLoading } = trpc.userAttestation.getUserAttestation.useQuery({ attestationId });
    const { data: timelineData } = trpc.userAttestation.getAttestationTimeline.useQuery({ attestationId });

    // Mutations
    const {
        mutate: reviewAttestation,
        isPending: isReviewing,
        variables: reviewVariables,
    } = trpc.userAttestation.reviewUserAttestation.useMutation({
        onSuccess: () => {
            message.success('Attestation reviewed successfully');
            trpcUtils.userAttestation.getUserAttestation.invalidate({ attestationId });
            trpcUtils.userAttestation.getAttestationTimeline.invalidate({ attestationId });
            trpcUtils.userAttestation.listUserAttestations.invalidate();
            reviewForm.resetFields();
            formDirty.markClean();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to review attestation');
        },
    });

    const { mutate: reopenAttestation, isPending: isReopening } =
        trpc.userAttestation.reopenUserAttestation.useMutation({
            onSuccess: () => {
                message.success('Attestation reopened');
                trpcUtils.userAttestation.getUserAttestation.invalidate({ attestationId });
                trpcUtils.userAttestation.getAttestationTimeline.invalidate({ attestationId });
                trpcUtils.userAttestation.listUserAttestations.invalidate();
            },
            onError: (error) => {
                message.error(error.message || 'Failed to reopen attestation');
            },
        });

    const { mutate: addComment, isPending: isAddingComment } = trpc.userAttestation.addAttestationComment.useMutation({
        onSuccess: () => {
            message.success('Comment added');
            trpcUtils.userAttestation.getAttestationTimeline.invalidate({ attestationId });
            reviewForm.resetFields();
            formDirty.markClean();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to add comment');
        },
    });

    const { mutate: cancelAttestation, isPending: isCancelling } =
        trpc.userAttestation.cancelUserAttestation.useMutation({
            onSuccess: () => {
                message.success('Attestation cancelled');
                setShowCancelModal(false);
                trpcUtils.userAttestation.getUserAttestation.invalidate({ attestationId });
                trpcUtils.userAttestation.getAttestationTimeline.invalidate({ attestationId });
                trpcUtils.userAttestation.listUserAttestations.invalidate();
            },
            onError: (error) => {
                message.error(error.message || 'Failed to cancel attestation');
            },
        });

    // Handlers
    const handleReview = (status: 'PASSED' | 'FAILED') => {
        void validateReviewCommentForm(
            reviewForm,
            (values) => {
                reviewAttestation({
                    attestationId,
                    status,
                    comment: values.comment,
                });
            },
            () => message.error('Unable to validate review comment'),
        );
    };

    const handleReopenWithConfirm = () => {
        confirmDestructive({
            title: 'Reopen attestation?',
            content:
                'This will reopen the attestation and allow the recipient to submit a new response. The current submission data will be retained.',
            okText: 'Reopen',
            onOk: () => reopenAttestation({ attestationId }),
        });
    };

    const handleAddComment = () => {
        void validateReviewCommentForm(
            reviewForm,
            (values) => {
                addComment({
                    attestationId,
                    comment: values.comment,
                });
            },
            () => message.error('Unable to validate review comment'),
        );
    };

    const handleCancel = () => {
        setShowCancelModal(true);
    };

    const handleConfirmCancel = (reason: string) => {
        cancelAttestation({
            attestationId,
            reason,
        });
    };

    // Can cancel when status is SENT or OVERDUE (form not yet submitted)
    const canCancel = attestation?.status === 'SENT' || attestation?.status === 'OVERDUE';
    // Reminders are an attestation reporting action, gated on the server as `remindUserAttestations` is.
    const canRemind = canCancel && reportingEnabled && can('user_attestation:update');

    // Can reopen when attestation has been reviewed or received
    // TODO: CUR-2110 - Reopen functionality temporarily hidden
    const canReopen = false;
    // const canReopen =
    //     attestation?.status === 'PASSED' || attestation?.status === 'FAILED' || attestation?.status === 'RECEIVED';
    const hasTabBarActions = canReopen || canRemind || canCancel;

    return (
        <PageController route={Route}>
            <OverlayPage
                title={
                    attestation?.Recipient?.fullName
                        ? `User attestation for ${attestation.Recipient.fullName}`
                        : 'User attestation'
                }
                isDirty={formDirty.isDirty}
                afterClose={() => navigate({ moveInMatches: -1 })}
                size="large"
            >
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Loading size="large" />
                    </div>
                ) : attestation ? (
                    <BasicTabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        tabBarExtraContent={
                            hasTabBarActions ? (
                                <Space>
                                    {canReopen && (
                                        <Button
                                            type="danger"
                                            onClick={handleReopenWithConfirm}
                                            loading={isReopening}
                                            icon={<RefreshCw />}
                                        >
                                            Reopen
                                        </Button>
                                    )}
                                    {canRemind && <AttestationReminderButton attestationId={attestationId} />}
                                    {canCancel && (
                                        <Button
                                            type="danger"
                                            onClick={handleCancel}
                                            loading={isCancelling}
                                            icon={<Ban />}
                                        >
                                            Cancel attestation
                                        </Button>
                                    )}
                                </Space>
                            ) : undefined
                        }
                        items={[
                            {
                                key: 'overview',
                                label: 'Overview',
                                children: <OverviewTab attestation={attestation} formatter={formatter} />,
                            },
                            {
                                key: 'submission',
                                label: 'Submission',
                                children: <SubmissionTab attestation={attestation} formatter={formatter} />,
                            },
                            {
                                key: 'review',
                                label: 'Review',
                                children: (
                                    <ReviewTab
                                        attestation={attestation}
                                        timelineData={timelineData}
                                        formatter={formatter}
                                        reviewForm={reviewForm}
                                        reviewFormDirtyProps={formDirty.formProps}
                                        pendingDecision={isReviewing ? reviewVariables?.status : undefined}
                                        isAddingComment={isAddingComment}
                                        onReview={handleReview}
                                        onAddComment={handleAddComment}
                                    />
                                ),
                            },
                        ]}
                    />
                ) : (
                    <div className="py-12 text-center">
                        <Text type="secondary">Attestation not found</Text>
                    </div>
                )}
            </OverlayPage>

            <CancelAttestationModal
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleConfirmCancel}
                isLoading={isCancelling}
            />
        </PageController>
    );
}
